import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';

// Cache configuration
const CACHE_CONFIG = {
  QUESTIONS_CACHE_TTL: 7 * 24 * 60 * 60 * 1000, // 7 days
  PHCS_CACHE_TTL: 7 * 24 * 60 * 60 * 1000, // 7 days
  SUBMISSIONS_CACHE_TTL: 5 * 60 * 1000, // 5 minutes
  MAX_RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 second
  MAX_CACHE_SIZE_MB: 40, // Keep under 50MB limit
  MAX_CACHE_SIZE_BYTES: 40 * 1024 * 1024
};

// Simple in-memory cache for faster access
const memoryCache = new Map();

// Calculate approximate size of data in bytes
const getDataSize = (data) => {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch (error) {
    return 0;
  }
};

// Get total cache size in bytes
const getTotalCacheSize = () => {
  let total = 0;
  
  // Memory cache size
  for (const [, value] of memoryCache) {
    total += getDataSize(value.data);
  }
  
  // localStorage size
  try {
    for (const key in localStorage) {
      if (key.startsWith('offline_cache_')) {
        total += getDataSize(localStorage.getItem(key));
      }
    }
  } catch (error) {
  }
  
  return total;
};

// Remove oldest items from memory cache
const cleanupOldestCache = () => {
  if (memoryCache.size === 0) return;
  
  let oldest = { key: null, timestamp: Date.now(), size: 0 };
  
  for (const [key, value] of memoryCache) {
    if (value.timestamp < oldest.timestamp) {
      oldest = { key, timestamp: value.timestamp, size: value.size };
    }
  }
  
  if (oldest.key) {
    memoryCache.delete(oldest.key);
    console.log(`Removed ${oldest.key} from memory cache (${(oldest.size / 1024).toFixed(2)}KB freed)`);
  }
};

// Remove oldest items from localStorage
const clearOldestLocalStorageCache = () => {
  try {
    const items = [];
    for (const key in localStorage) {
      if (key.startsWith('offline_cache_')) {
        const stored = localStorage.getItem(key);
        const { timestamp } = JSON.parse(stored);
        items.push({ key, timestamp });
      }
    }
    
    // Sort by timestamp and remove oldest
    items.sort((a, b) => a.timestamp - b.timestamp);
    if (items.length > 0) {
      localStorage.removeItem(items[0].key);
      console.log(`Removed oldest item from localStorage`);
    }
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

// Get cached data from memory or localStorage
const getCachedData = (key, ttl) => {
  // Check memory cache first
  const memoryData = memoryCache.get(key);
  if (memoryData && Date.now() - memoryData.timestamp < ttl) {
    return memoryData.data;
  }

  // Fallback to localStorage
  try {
    const stored = localStorage.getItem(`offline_cache_${key}`);
    if (stored) {
      const { data, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp < ttl) {
        // Store in memory cache for faster access
        memoryCache.set(key, { data, timestamp });
        return data;
      }
    }
  } catch (error) {
  }

  return null;
};

// Set cached data in both memory and localStorage
const setCachedData = (key, data) => {
  const timestamp = Date.now();
  const dataSize = getDataSize(data);
  
  // Check if this single item exceeds limit
  if (dataSize > CACHE_CONFIG.MAX_CACHE_SIZE_BYTES) {
    console.warn(`Data for key "${key}" is ${(dataSize / 1024 / 1024).toFixed(2)}MB - exceeds limit`);
    return false;
  }
  
  // Check total cache size and cleanup if needed
  const currentSize = getTotalCacheSize();
  if (currentSize + dataSize > CACHE_CONFIG.MAX_CACHE_SIZE_BYTES) {
    cleanupOldestCache();
  }
  
  // Store in memory cache
  memoryCache.set(key, { data, timestamp, size: dataSize });
  
  // Store in localStorage
  try {
    localStorage.setItem(`offline_cache_${key}`, JSON.stringify({ data, timestamp }));
  } catch (error) {
    console.error('localStorage full, clearing old data:', error);
    clearOldestLocalStorageCache();
    try {
      localStorage.setItem(`offline_cache_${key}`, JSON.stringify({ data, timestamp }));
    } catch (retryError) {
      console.error('Still unable to store:', retryError);
      return false;
    }
  }
  
  return true;
};

// Clear cached data
const clearCachedData = (key) => {
  memoryCache.delete(key);
  try {
    localStorage.removeItem(`offline_cache_${key}`);
  } catch (error) {
  }
};

// Retry wrapper for API calls
const withRetry = async (fn, maxAttempts = CACHE_CONFIG.MAX_RETRY_ATTEMPTS) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt < maxAttempts) {
        // Exponential backoff
        const delay = CACHE_CONFIG.RETRY_DELAY * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

// Generic offline-first data fetch hook
export const useOfflineFirstData = (cacheKey, fetchFn, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);

  const {
    ttl = CACHE_CONFIG.QUESTIONS_CACHE_TTL,
    enabled = true,
    refreshOnWindowFocus = false,
    backgroundRefresh = false
  } = options;

  // Fetch data with offline-first strategy
  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      // Try cache first (unless force refresh)
      if (!forceRefresh) {
        const cachedData = getCachedData(cacheKey, ttl);
        if (cachedData) {
          setData(cachedData);
          setIsFromCache(true);
          setLastUpdated(new Date());
          setLoading(false);
          return cachedData;
        }
      }

      // Fetch from network
      setIsFromCache(false);
      
      const networkData = await withRetry(fetchFn);
      
      // Cache the successful response
      setCachedData(cacheKey, networkData);
      setData(networkData);
      setLastUpdated(new Date());
      
    } catch (err) {
      
      // Try to fallback to cache even if expired
      const fallbackData = getCachedData(cacheKey, ttl * 10); // Extend TTL for fallback
      if (fallbackData) {
        setData(fallbackData);
        setIsFromCache(true);
        setLastUpdated(new Date());
        setError({ 
          message: 'Using cached data due to network error', 
          isNetworkError: true,
          originalError: err 
        });
      } else {
        setError({
          message: 'Failed to load data and no cache available',
          isNetworkError: true,
          originalError: err
        });
      }
    } finally {
      setLoading(false);
    }
  }, [cacheKey, fetchFn, ttl, enabled]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Refresh on window focus (optional)
  useEffect(() => {
    if (!refreshOnWindowFocus) return;

    const handleFocus = () => {
      // Only refresh if data is older than TTL
      if (!lastUpdated || Date.now() - lastUpdated.getTime() > ttl) {
        fetchData();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshOnWindowFocus, fetchData, lastUpdated, ttl]);

  // Background refresh (optional)
  useEffect(() => {
    if (!backgroundRefresh || !lastUpdated) return;

    const interval = setInterval(() => {
      // Only refresh if app is visible and data is older than TTL
      if (!document.hidden && Date.now() - lastUpdated.getTime() > ttl) {
        fetchData(true);
      }
    }, ttl / 2); // Check twice as often as TTL

    return () => clearInterval(interval);
  }, [backgroundRefresh, fetchData, lastUpdated, ttl]);

  // Listen for online events to refresh data
  useEffect(() => {
    const handleOnline = () => {
      if (error?.isNetworkError) {
        fetchData(true);
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [fetchData, error]);

  return {
    data,
    loading,
    error,
    lastUpdated,
    isFromCache,
    refresh: () => fetchData(true),
    clearCache: () => {
      clearCachedData(cacheKey);
      setData(null);
      setLastUpdated(null);
      setIsFromCache(false);
    }
  };
};

// Specific hooks for different data types
export const useOfflineFirstQuestions = (userKey, fetchFn) => {
  return useOfflineFirstData(
    `questions_${userKey}`,
    fetchFn,
    {
      ttl: CACHE_CONFIG.QUESTIONS_CACHE_TTL,
      refreshOnWindowFocus: true,
      backgroundRefresh: true
    }
  );
};

export const useOfflineFirstPHCs = (fetchFn) => {
  return useOfflineFirstData(
    'phcs',
    fetchFn,
    {
      ttl: CACHE_CONFIG.PHCS_CACHE_TTL,
      refreshOnWindowFocus: false,
      backgroundRefresh: false
    }
  );
};

export const useOfflineFirstSubmissions = (fetchFn) => {
  return useOfflineFirstData(
    'submissions',
    fetchFn,
    {
      ttl: CACHE_CONFIG.SUBMISSIONS_CACHE_TTL,
      refreshOnWindowFocus: true,
      backgroundRefresh: true
    }
  );
};

// Export cache utility functions for external use
export { getCachedData, setCachedData, clearCachedData };

// Utility functions for cache management
export const clearAllOfflineCache = () => {
  // Clear memory cache
  memoryCache.clear();
  
  // Clear localStorage cache
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('offline_cache_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
  }
};

export const getCacheInfo = () => {
  const cacheKeys = Array.from(memoryCache.keys());
  const cacheSize = cacheKeys.length;
  
  return {
    memoryCacheSize: cacheSize,
    memoryCacheKeys: cacheKeys,
    localStorageSize: Object.keys(localStorage).filter(key => key.startsWith('offline_cache_')).length
  };
};

// Preload critical data
export const preloadCriticalData = async (userKey) => {
  
  // This can be called during app initialization to ensure critical data is cached
  try {
    // Preload questions for all user types
    const userTypes = ['community', 'health-worker', 'wdc'];
    for (const userType of userTypes) {
      const cacheKey = `questions_${userType}`;
      if (!getCachedData(cacheKey, CACHE_CONFIG.QUESTIONS_CACHE_TTL)) {
        // This would need to be implemented based on your API structure
      }
    }
    
    // Preload PHCs
    if (!getCachedData('phcs', CACHE_CONFIG.PHCS_CACHE_TTL)) {
      // This would need to be implemented based on your API structure
    }
  } catch (error) {
  }
};